import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../api';
import { printLabels, waybillsForOrder } from '../labels';

export default function Orders() {
  // reprinting the label for an order that already shipped
  const [printingId, setPrintingId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [sortOrder, search]);

  // Reprint an already-manifested order's label — a lost or spoiled label
  // shouldn't mean digging through the Manifest History to find its batch.
  const handlePrintLabel = async (order) => {
    const waybills = waybillsForOrder(order);
    if (waybills.length === 0) return;
    setPrintingId(order._id);
    try {
      const { skipped, popupBlocked } = await printLabels(waybills);
      if (skipped.length) {
        alert(`⚠ Not ready at the courier yet, left out — retry shortly:\n${skipped.join(', ')}`);
      }
      if (popupBlocked) alert('The label opened in a blocked popup — allow popups for this site.');
    } catch (e) {
      alert('Label could not be opened: ' + e.message);
    } finally {
      setPrintingId(null);
    }
  };

  const fetchOrders = async () => {
    try {
      const params = new URLSearchParams();
      params.append('sort', sortOrder);
      if (search) params.append('search', search);
      const res = await axios.get(`${API}/api/orders?${params.toString()}`);
      setOrders(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${API}/api/shopify/sync`);
      const results = res.data.results;
      const fulfilled = results.filter(r => r.fulfilmentStatus === 'fulfilled').length;
      const unfulfillable = results.filter(r => r.fulfilmentStatus === 'unfulfillable').length;
      const partial = results.filter(r => r.fulfilmentStatus === 'partially_fulfilled').length;
      const skipped = results.filter(r => r.status === 'skipped').length;
      setSyncResult({ fulfilled, unfulfillable, partial, skipped, total: results.length });
      await fetchOrders();
    } catch (e) {
      setSyncResult({ error: 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const filtered = orders.filter(o =>
    filter === 'all' ? true : o.fulfilmentStatus === filter
  );

  const total = orders.length;
  const fulfilled = orders.filter(o => o.fulfilmentStatus === 'fulfilled').length;
  const unfulfillable = orders.filter(o => o.fulfilmentStatus === 'unfulfillable').length;
  const pending = orders.filter(o => o.fulfilmentStatus === 'pending').length;

  const statusBadge = (status) => {
    if (status === 'fulfilled') return <span className="badge badge-green">Fulfilled</span>;
    if (status === 'unfulfillable') return <span className="badge badge-red">Unfulfillable</span>;
    if (status === 'partially_fulfilled') return <span className="badge badge-yellow">Partial</span>;
    return <span className="badge badge-blue">Pending</span>;
  };

  const fulfilledFromBadge = (from) => {
    if (from === 'returns') return <span className="badge badge-blue">Returns</span>;
    if (from === 'blanks_and_prints') return <span className="badge badge-green">Blanks + Prints</span>;
    return <span className="badge badge-red">Pending</span>;
  };

  return (
    <div>
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Total Orders</div>
          <div className="stat-value">{total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Fulfilled</div>
          <div className="stat-value" style={{ color: '#2e7d50' }}>{fulfilled}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Unfulfillable</div>
          <div className="stat-value" style={{ color: '#b3261e' }}>{unfulfillable}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pending</div>
          <div className="stat-value" style={{ color: '#7a6a12' }}>{pending}</div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div className="card-title" style={{ margin: 0 }}>Orders</div>
          <button
            className="btn-primary"
            onClick={handleSync}
            disabled={syncing}
            style={{ padding: '10px 20px', fontSize: '14px' }}
          >
            {syncing ? 'Syncing from Shopify...' : '⟳ Sync from Shopify'}
          </button>
        </div>

        {syncResult && (
          <div style={{
            background: syncResult.error ? '#fbe9e7' : '#e8f3ec',
            border: `1px solid ${syncResult.error ? '#b3261e' : '#2e7d50'}`,
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            fontSize: '13px',
            color: syncResult.error ? '#b3261e' : '#2e7d50'
          }}>
            {syncResult.error ? syncResult.error : (
              `Sync complete — ${syncResult.total} orders processed: ${syncResult.fulfilled} fulfilled, ${syncResult.partial} partial, ${syncResult.unfulfillable} unfulfillable, ${syncResult.skipped} skipped`
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            placeholder="Search order #, customer, or design..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '280px' }}
          />
          <select value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All Orders</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="unfulfillable">Unfulfillable</option>
            <option value="pending">Pending</option>
            <option value="partially_fulfilled">Partially Fulfilled</option>
          </select>
          <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>

        {loading ? <p style={{ color: '#93827a' }}>Loading...</p> : (
          <table>
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Status</th>
                <th>Date</th>
                <th>Label</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(order => (
                <tr key={order._id}>
                  <td style={{ fontFamily: 'monospace', color: '#8c3242' }}>
                    {order.shopifyOrderNumber}
                  </td>
                  <td>{order.customerName}</td>
                  <td>
                    {order.items.map((item, i) => (
                      <div key={i} style={{ marginBottom: '4px', fontSize: '13px' }}>
                        <span style={{ fontWeight: '500' }}>{item.designName}</span>
                        {' '}
                        <span style={{ color: '#93827a' }}>({item.designCode})</span>
                        {' · '}{item.color} {item.size}
                        {' · '}{fulfilledFromBadge(item.fulfilledFrom)}
                      </div>
                    ))}
                  </td>
                  <td>{statusBadge(order.fulfilmentStatus)}</td>
                  <td style={{ color: '#93827a', fontSize: '13px' }}>
                    {new Date(order.createdAt).toLocaleDateString('en-IN')}
                  </td>
                  <td>
                    {(() => {
                      const waybills = waybillsForOrder(order);
                      if (waybills.length === 0) {
                        return <span style={{ color: '#b0a296', fontSize: 12 }}>not shipped</span>;
                      }
                      return (
                        <button
                          onClick={() => handlePrintLabel(order)}
                          disabled={printingId === order._id}
                          title={`Reprint the 4×6 label${waybills.length > 1 ? 's' : ''} for ${waybills.join(', ')}`}
                          style={{
                            fontSize: 12, padding: '4px 10px', borderRadius: 6,
                            cursor: printingId === order._id ? 'default' : 'pointer',
                            background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {printingId === order._id
                            ? 'Opening…'
                            : `🏷 Label${waybills.length > 1 ? ` ×${waybills.length}` : ''}`}
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ color: '#93827a', textAlign: 'center' }}>
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}